import { useRouter } from 'next/router';
import {
  Box,
  Button,
  designTokens,
  Divider,
  FormControl,
  FormControlText,
  FormGroup,
  Input,
  Panel,
  PanelBody,
  PanelHeader,
  TextLink
} from '@itandi/itandi-bb-ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { create as login } from '~/utils/api/vendors/sessions/VendorCompany';
import { PATH as VENDOR_HOME_PATH } from '~/pages/vendors';
import { isSuccess } from '~/utils/api/api_handler';
import { getVendorPurposeUrl } from '~/utils/AuthenticateRenderer';
import { VendorWithoutAuthLayout } from '~/components/common/layouts/VendorWithoutAuthLayout';
import type { VendorCompanyLoginParams } from '~/models/VendorCompany';
import { vendorCompanyLoginSchema } from '~/models/VendorCompany';
import { VendorPasswordResetPanel } from './VendorPasswordResetPanel';

export const Page: React.FC = () => {
  const router = useRouter();
  const [isModeReset, setIsModeReset] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<VendorCompanyLoginParams>({
    resolver: yupResolver(vendorCompanyLoginSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const onSubmit = handleSubmit(
    async (values: VendorCompanyLoginParams): Promise<void> => {
      setLoginErrorMessage('');
      const result = await login({
        email: values.email,
        password: values.password
      });
      if (isSuccess(result)) {
        await router.push(getVendorPurposeUrl() ?? VENDOR_HOME_PATH);
      } else {
        setLoginErrorMessage(result.message || '');
      }
    }
  );

  return (
    <VendorWithoutAuthLayout>
      <Box mt={designTokens['spacing/l']} mx="auto" w="min(80%, 500px)">
        {isModeReset ? (
          <VendorPasswordResetPanel
            onResetMailSend={() => setIsModeReset(false)}
          />
        ) : (
          <Panel>
            <PanelHeader title="施工会社ログイン" />
            <PanelBody>
              <form noValidate onSubmit={onSubmit}>
                <FormGroup variant="Vertical">
                  <FormControl
                    isInvalid={!!errors.email || !!loginErrorMessage}
                    label="メールアドレス（ログインID）"
                  >
                    <Input
                      type="email"
                      {...register('email')}
                      autoComplete="username"
                      placeholder="例）sample@example.com"
                    />
                    <FormControlText error={errors.email?.message} />
                  </FormControl>
                  <FormControl
                    isInvalid={!!errors.password || !!loginErrorMessage}
                    label="パスワード"
                  >
                    <Input
                      {...register('password')}
                      autoComplete="current-password"
                      placeholder="パスワード"
                      type="password"
                    />
                    <FormControlText
                      error={errors.password?.message || loginErrorMessage}
                    />
                  </FormControl>
                </FormGroup>
                <Button
                  loading={isSubmitting}
                  marginY={designTokens['spacing/m']}
                  type="submit"
                  variant="primary"
                  width="fill"
                >
                  ログイン
                </Button>
              </form>
              <Divider marginY={designTokens['spacing/m']} />
              <TextLink
                onClick={() => {
                  setIsModeReset(true);
                  reset();
                }}
                variant="Primary"
              >
                パスワードをお忘れの方はこちら
              </TextLink>
            </PanelBody>
          </Panel>
        )}
      </Box>
    </VendorWithoutAuthLayout>
  );
};
